import { HttpEvent, HttpEventType } from '@angular/common/http';
import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject, takeUntil } from 'rxjs';
import { BaseUnsubscribeComponent } from '../../../../shared-new/components/base-unsubscribe/base-unsubscribe.component';
import { SpinnerService } from '../../../../shared-new/services/spinner.service';
import { StudyService } from '../../../../shared-new/services/study.service';
import { DownloadUtilsService } from 'src/app/services/download-utils-service';

@Component({
  selector: 'app-study-files-downloader',
  templateUrl: './study-files-downloader.component.html',
  styleUrls: ['./study-files-downloader.component.scss']
})
export class StudyFilesDownloaderComponent extends BaseUnsubscribeComponent implements OnInit {
  @Input() fileInfo: any;
  constructor(public modal: NgbActiveModal,
    private readonly studyService: StudyService,
    private downloadUtilsService: DownloadUtilsService) {
    super();
  }

  ngOnInit(): void {
  }

  closePopup(): void {
    this.modal.close(JSON.stringify(this.fileInfo));
  }

  public zipProgress: boolean = false;
  public downloadedZipBytes: number = 0;
  private cancel$ = new Subject<void>();

  async downloadStudyFile(url: string, fileName: string) {
    this.downloadUtilsService.triggerFileDownload(url, fileName);
  }

  downloadDicomFiles(isImg: boolean): void {
    this.zipProgress = true;
    this.isPreviewFileMode = isImg;
    this.cancel$ = new Subject<void>();
    if (this.fileInfo.mode === 'study') {
      this.studyService.downloadDicomFiles(this.fileInfo.studyId, isImg, this.cancel$)
        .pipe(takeUntil(this.cancel$))
        .subscribe({
          next: (event: HttpEvent<any>) => {
            let fileName = `study_${this.fileInfo.studyId}_dicom_files.zip`; // Default name
            this.downloadBytes(event, fileName);
          },
          error: () => {
            this.zipProgress = false; // Hide spinner on error
          },
          complete: () => {
            this.zipProgress = false; // Ensure spinner is hidden
          }
        });
    }
    else if (this.fileInfo.mode === 'submissions') {
      this.studyService.downloadDicomFilesForSubmission(this.fileInfo.studyFileIds, isImg, this.cancel$)
        .pipe(takeUntil(this.cancel$))
        .subscribe({
          next: (event: HttpEvent<any>) => {
            let fileName = `study_${this.fileInfo.studyId}_dicom_files.zip`; // Default name
            this.downloadBytes(event, fileName);
          },
          error: () => {
            this.zipProgress = false; // Hide spinner on error
          },
          complete: () => {
            this.zipProgress = false; // Ensure spinner is hidden
          }
        });
    }
    else if (this.fileInfo.mode === 'share') {
      this.studyService.downloadDicomFilesForShared(this.fileInfo.protectedString, this.fileInfo.studyFileIds, isImg, this.cancel$)
        .pipe(takeUntil(this.cancel$))
        .subscribe({
          next: (event: HttpEvent<any>) => {
            let fileName = `study_${this.fileInfo.studyId}_dicom_files.zip`; // Default name
            this.downloadBytes(event, fileName);
          },
          error: () => {
            this.zipProgress = false; // Hide spinner on error
          },
          complete: () => {
            this.zipProgress = false; // Ensure spinner is hidden
          }
        });
    }
  }

  // isPreviewFileMode is set when we download the dicom or preview files
  private isPreviewFileMode: boolean = false;
  public isTotalSizeAvailable(): boolean {
    return this.isPreviewFileMode ? this.fileInfo?.allJpegSizeInBytes !== null :
      this.fileInfo?.allDicomSizeInBytes !== null;
  }

  public totalSizeInBytes(): number {
    return (this.isPreviewFileMode ? this.fileInfo?.allJpegSizeInBytes : this.fileInfo?.allDicomSizeInBytes) ?? 0;
  }

  private downloadBytes(event: HttpEvent<any>, fileName: string) {
    this.downloadedZipBytes = (event as any).loaded as unknown as number;
    if (isNaN(this.downloadedZipBytes)) {
      this.downloadedZipBytes = 0;
    }

    if (event.type === HttpEventType.Response) {
      const contentDisposition = event.headers?.get('Content-Disposition');


      if (contentDisposition) {
        const matches = contentDisposition.match(/filename="?([^"]+)"?/);
        if (matches?.length > 1) {
          fileName = matches[1]; // Extracted filename from header
        }
      }

      const blob = event.body!;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      window.URL.revokeObjectURL(url);
      this.zipProgress = false;
    }
  }

  formatSize(bytes: number): string {
    if (bytes < 1024) {
      return `${bytes} B`; // bytes
    } else if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(2)} KB`; // Kilobytes
    } else {
      return `${(bytes / (1024 * 1024)).toFixed(2)} MB`; // Megabytes
    }
  }

  cancelDownload() {
    this.cancel$.next(); // Cancels the download
    this.cancel$.complete();
    this.zipProgress = false;
  }

  override ngOnDestroy() {
    this.cancel$.next();
    this.cancel$.complete();
  }

}
